// Inbound — the three stages of receiving goods:
//   GRN      record what arrived (no stock)                        → GrnTab in WmsPage.tsx
//   PUTAWAY  split Good/Bad, choose a bin, POST it into inventory  → this file
//   QC       second inspection of what is already on the shelf     → this file
//
// Putaway and QC are worklists: they list only the inwards waiting at that stage, and open the same
// line editor with the fields that stage owns.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, PackageCheck, ClipboardCheck } from 'lucide-react';
import { api } from '@/api/client';
import {
  Badge, F, WmsCard, WmsDateRange, WmsPageBar, WmsSearch,
  useSearchPaged, useWmsViewOnly, wmsInDateRange,
} from './WmsShared';

/** Seed the editable line state for whichever stage is open. */
function seedLines(grn: any, stage: 'putaway' | 'qc') {
  return (grn.items ?? []).map((i: any) => {
    const received = Number(i.receivedQty ?? 0);
    if (stage === 'putaway') {
      const done = i.putawayStatus === 'DONE';
      // A line not yet put away pre-fills as all-Good, so the common case is one click.
      const badQty = done ? Number(i.badQty ?? 0) : 0;
      return {
        id: i.id, sku: i.product?.sku, name: i.product?.name, receivedQty: received,
        batchNo: i.batchNo ?? '', uom: i.uom ?? '',
        goodQty: done ? Number(i.goodQty ?? 0) : received, badQty,
        bin: i.bin ?? '', putawayStatus: i.putawayStatus ?? 'PENDING',
      };
    }
    // QC pre-fills with what putaway posted — saving unchanged is a deliberate no-op.
    const pending = !i.qcStatus || i.qcStatus === 'PENDING';
    const bad = pending ? Number(i.badQty ?? 0) : Number(i.qcBadQty ?? 0);
    const hold = pending ? 0 : Number(i.qcHoldQty ?? 0);
    const rejected = pending ? 0 : Number(i.qcRejectedQty ?? 0);
    return {
      id: i.id, sku: i.product?.sku, name: i.product?.name, receivedQty: received,
      batchNo: i.batchNo ?? '', bin: i.bin ?? '',
      postedGoodQty: Number(i.goodQty ?? 0), postedBadQty: Number(i.badQty ?? 0),
      qcGoodQty: pending ? Number(i.goodQty ?? 0) : Number(i.qcGoodQty ?? 0),
      qcBadQty: bad, qcHoldQty: hold, qcRejectedQty: rejected,
      // A pre-filled line is judged by whichever non-good bucket carries stock.
      qcStatus: pending ? (bad > 0 ? 'DAMAGED' : 'PASSED') : (i.qcStatus === 'PARTIAL' ? 'DAMAGED' : i.qcStatus),
      qcRemarks: i.qcRemarks ?? '',
    };
  });
}

// The four things QC can conclude about a line. Only PASSED leaves stock sellable — Damaged, Rejected
// and Hold all take it OUT of available, which is why each of them needs a written reason.
const QC_OUTCOMES = [
  { value: 'PASSED', label: 'Passed', bucket: 'qcGoodQty' },
  { value: 'DAMAGED', label: 'Damaged', bucket: 'qcBadQty' },
  { value: 'REJECTED', label: 'Failed / Rejected', bucket: 'qcRejectedQty' },
  { value: 'HOLD', label: 'Hold', bucket: 'qcHoldQty' },
] as const;
const QC_BUCKETS = ['qcGoodQty', 'qcBadQty', 'qcHoldQty', 'qcRejectedQty'] as const;

// ───────────────────────────── PUTAWAY ─────────────────────────────

export function PutawayTab() {
  return (
    <InboundWorklist
      stage="putaway"
      title="Putaway — move received stock into inventory"
      subtitle="Enter the Good / Bad split and the bin. Saving posts the stock: Good → available, Bad → DAMAGE."
      emptyText="Nothing waiting to be put away"
      isPending={(g: any) => g.status === 'PENDING_PUTAWAY'}
      bulkPath="/wms/grn/bulk-putaway"
      bulkLabel="Putaway — All Good"
      bulkDone="Put away (all Good)"
      Icon={PackageCheck}
    />
  );
}

// ─────────────────────────────── QC ────────────────────────────────

export function InboundQcTab() {
  return (
    <InboundWorklist
      stage="qc"
      title="QC — second check on stock already put away"
      subtitle="Re-state the Good / Bad split. Only the difference against putaway is moved (available ↔ damaged)."
      emptyText="Nothing waiting for QC"
      isPending={(g: any) => g.status === 'PENDING_QC'}
      bulkPath="/wms/grn/bulk-qc"
      bulkLabel="Pass QC as put away"
      bulkDone="QC completed"
      Icon={ClipboardCheck}
    />
  );
}

// ───────────────────── shared worklist for both stages ─────────────────────

function InboundWorklist({ stage, title, subtitle, emptyText, isPending, bulkPath, bulkLabel, bulkDone, Icon }: {
  stage: 'putaway' | 'qc';
  title: string; subtitle: string; emptyText: string;
  isPending: (g: any) => boolean;
  bulkPath: string; bulkLabel: string; bulkDone: string;
  Icon: any;
}) {
  const qc = useQueryClient();
  const viewOnly = useWmsViewOnly();
  const [active, setActive] = useState<any>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [showDone, setShowDone] = useState(false);
  const [sel, setSel] = useState<Set<number>>(new Set());

  const { data = [] } = useQuery({ queryKey: ['wms-grns'], queryFn: async () => (await api.get('/wms/grn')).data as any[] });
  const dateFiltered = (data as any[]).filter((g) => wmsInDateRange(g.createdAt, from, to));
  const rows = dateFiltered.filter((g) => (showDone ? true : isPending(g)) && g.status !== 'CANCELLED');

  const { q, setQ, page, setPage, total, pageRows } = useSearchPaged(
    rows,
    (g: any, t) => [g.grnNo, g.supplier?.name, g.supplierName, g.invoiceNo, g.poNumber, g.status].some((v) => String(v ?? '').toLowerCase().includes(t)),
  );

  const toggle = (id: number) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  // Rows on the current page that this stage can still act on — what "select all" covers.
  const selectableOnPage = pageRows.filter((g: any) => isPending(g)).map((g: any) => Number(g.id));
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['wms-grns'] });
    qc.invalidateQueries({ queryKey: ['wms-stock'] });
  };

  const bulk = useMutation({
    mutationFn: async () => (await api.post(bulkPath, { grnIds: Array.from(sel) })).data,
    onSuccess: (r: any) => {
      setSel(new Set());
      refresh();
      alert(`${bulkDone} for ${r.passed} inward(s)${r.failed ? `, ${r.failed} failed:\n${(r.errors ?? []).map((e: any) => `#${e.grnId}: ${e.reason}`).join('\n')}` : '.'}`);
    },
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Bulk action failed'),
  });

  return (
    <WmsCard
      title={<span className="flex items-center gap-2"><Icon size={16} /> {title}</span>}
      actions={
        <>
          <WmsSearch value={q} onChange={setQ} placeholder="Search GRN no / supplier / invoice" className="w-72" />
          <WmsDateRange from={from} to={to} onFrom={(v) => { setFrom(v); setPage(1); }} onTo={(v) => { setTo(v); setPage(1); }} label="GRN date" />
        </>
      }
    >
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <span className="text-xs text-slate-500">{subtitle}</span>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 ml-auto">
          <input type="checkbox" checked={showDone} onChange={(e) => { setShowDone(e.target.checked); setPage(1); setSel(new Set()); }} />
          Show completed too
        </label>
        {/* The bulk action is always on screen — previously it only appeared once rows were already
            ticked, so there was nothing to tell anyone it existed. */}
        {!viewOnly && (
          <button onClick={() => bulk.mutate()} disabled={bulk.isPending || sel.size === 0}
            title={sel.size === 0 ? `Tick the inwards you want to ${stage === 'putaway' ? 'put away' : 'QC'}, then click here` : undefined}
            className="bg-amber-600 text-white px-3 py-1.5 rounded text-sm disabled:opacity-40">
            {bulk.isPending ? 'Working…' : `${bulkLabel}${sel.size ? ` (${sel.size})` : ''}`}
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50"><tr>
            {!viewOnly && (
              <th className="p-2 w-8">
                {/* Select-all across the rows currently on screen that are still waiting at this stage. */}
                <input
                  type="checkbox"
                  title="Select all on this page"
                  checked={selectableOnPage.length > 0 && selectableOnPage.every((id) => sel.has(id))}
                  onChange={(e) => setSel((s) => {
                    const n = new Set(s);
                    if (e.target.checked) selectableOnPage.forEach((id) => n.add(id));
                    else selectableOnPage.forEach((id) => n.delete(id));
                    return n;
                  })}
                />
              </th>
            )}
            <th className="p-2 text-left">GRN No</th><th className="text-left">Supplier</th><th className="text-left">Invoice</th>
            <th className="text-right">Qty</th><th className="text-right">Good</th><th className="text-right">Bad</th>
            <th>Status</th><th>Date</th><th />
          </tr></thead>
          <tbody>
            {total === 0
              ? <tr><td colSpan={viewOnly ? 8 : 10} className="text-center py-6 text-slate-400">{q ? 'No matches' : emptyText}</td></tr>
              : pageRows.map((g: any) => {
                const good = (g.items ?? []).reduce((s: number, i: any) => s + Number(i.goodQty ?? 0), 0);
                const bad = (g.items ?? []).reduce((s: number, i: any) => s + Number(i.badQty ?? 0), 0);
                return (
                  <tr key={g.id} className="border-t cursor-pointer hover:bg-slate-50" onClick={() => setActive(g)}>
                    {!viewOnly && (
                      <td className="p-2" onClick={(e) => e.stopPropagation()}>
                        {isPending(g) && <input type="checkbox" checked={sel.has(Number(g.id))} onChange={() => toggle(Number(g.id))} />}
                      </td>
                    )}
                    <td className="p-2 font-mono text-xs">{g.grnNo}</td>
                    <td>{g.supplier?.name ?? g.supplierName ?? '—'}</td>
                    <td className="text-xs">{g.invoiceNo ?? '—'}</td>
                    <td className="text-right">{g.totalQty}</td>
                    <td className="text-right text-emerald-700">{good || '—'}</td>
                    <td className="text-right text-red-600">{bad || '—'}</td>
                    <td><Badge text={g.status} /></td>
                    <td className="text-xs text-slate-500">{g.createdAt?.slice(0, 10)}</td>
                    <td className="text-xs text-emerald-700">{isPending(g) ? `${stage === 'putaway' ? 'Putaway' : 'QC'} →` : 'Open →'}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      <WmsPageBar page={page} total={total} onPage={setPage} />

      {active && <InboundStageModal grn={active} stage={stage} viewOnly={viewOnly} onClose={() => setActive(null)} onSaved={() => { refresh(); setActive(null); }} />}
    </WmsCard>
  );
}

// ───────────────────────── the line editor ─────────────────────────

function InboundStageModal({ grn, stage, viewOnly, onClose, onSaved }: any) {
  // Always re-read the GRN so the modal edits fresh lines (the list payload can be a stale cache).
  const { data: fresh } = useQuery({ queryKey: ['wms-grn', grn.id], queryFn: async () => (await api.get(`/wms/grn/${grn.id}`)).data });
  const g = fresh ?? grn;
  const [items, setItems] = useState<any[]>(() => seedLines(grn, stage));
  const [seededFor, setSeededFor] = useState<any>(null);
  // Re-seed once the fresh copy lands (React-friendly: derive during render, not in an effect).
  if (fresh && seededFor !== fresh) { setSeededFor(fresh); setItems(seedLines(fresh, stage)); }

  const setIt = (idx: number, k: string, v: any) => setItems((arr) => arr.map((x, i) => i === idx ? { ...x, [k]: v } : x));

  // ── PUTAWAY: Good and Bad are two halves of Received — editing either recomputes the other.
  const setGood = (idx: number, val: any) => setItems((arr) => arr.map((x, i) => {
    if (i !== idx) return x;
    const good = Math.max(0, Math.min(Number(x.receivedQty ?? 0), Number(val) || 0));
    return { ...x, goodQty: good, badQty: Number(x.receivedQty ?? 0) - good };
  }));
  const setBad = (idx: number, val: any) => setItems((arr) => arr.map((x, i) => {
    if (i !== idx) return x;
    const bad = Math.max(0, Math.min(Number(x.receivedQty ?? 0), Number(val) || 0));
    return { ...x, badQty: bad, goodQty: Number(x.receivedQty ?? 0) - bad };
  }));

  // ── QC: four buckets that must total Received. Editing a non-good bucket re-derives Good, so the
  // line always balances; picking a status puts the WHOLE received quantity into that bucket.
  const setQcBucket = (idx: number, key: typeof QC_BUCKETS[number], val: any) => setItems((arr) => arr.map((x, i) => {
    if (i !== idx) return x;
    const received = Number(x.receivedQty ?? 0);
    const next = { ...x, [key]: Math.max(0, Math.min(received, Number(val) || 0)) };
    if (key !== 'qcGoodQty') {
      next.qcGoodQty = Math.max(0, received - Number(next.qcBadQty || 0) - Number(next.qcHoldQty || 0) - Number(next.qcRejectedQty || 0));
    }
    return next;
  }));
  const setQcStatus = (idx: number, status: string) => setItems((arr) => arr.map((x, i) => {
    if (i !== idx) return x;
    const received = Number(x.receivedQty ?? 0);
    const target = QC_OUTCOMES.find((o) => o.value === status)?.bucket ?? 'qcGoodQty';
    const cleared: any = { ...x, qcStatus: status, qcGoodQty: 0, qcBadQty: 0, qcHoldQty: 0, qcRejectedQty: 0 };
    cleared[target] = received;
    return cleared;
  }));

  const qcTotal = (i: any) => QC_BUCKETS.reduce((s, k) => s + Number(i[k] || 0), 0);
  // A line is only saveable when the quantities balance AND anything that did not pass carries a
  // remark — the backend enforces both, so blocking here just avoids a round trip.
  const lineValid = (i: any) => stage === 'putaway'
    ? Number(i.goodQty) + Number(i.badQty) === Number(i.receivedQty)
    : qcTotal(i) === Number(i.receivedQty)
      && (Number(i.qcGoodQty || 0) === Number(i.receivedQty) || !!String(i.qcRemarks ?? '').trim());
  const valid = items.length > 0 && items.every(lineValid);

  const save = useMutation({
    mutationFn: async () => {
      if (stage === 'putaway') {
        return (await api.post(`/wms/grn/${g.id}/putaway`, {
          items: items.map((i) => ({ id: i.id, goodQty: Number(i.goodQty), badQty: Number(i.badQty), bin: i.bin })),
        })).data;
      }
      return (await api.post(`/wms/grn/${g.id}/qc`, {
        items: items.map((i) => ({
          id: i.id,
          qcGoodQty: Number(i.qcGoodQty), qcBadQty: Number(i.qcBadQty),
          qcHoldQty: Number(i.qcHoldQty), qcRejectedQty: Number(i.qcRejectedQty),
          qcStatus: i.qcStatus, qcRemarks: i.qcRemarks,
        })),
      })).data;
    },
    onSuccess: onSaved,
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Save failed'),
  });

  const isPutaway = stage === 'putaway';
  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
      <div className="bg-white rounded-lg w-[1000px] max-h-[88vh] overflow-y-auto">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between">
          <div className="font-semibold flex items-center gap-2">{g.grnNo} · {isPutaway ? 'Putaway' : 'QC'} <Badge text={g.status} /></div>
          <button onClick={onClose}><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-4 gap-3 text-sm">
            <F label="Supplier"><div className="text-slate-700">{g.supplier?.name ?? g.supplierName ?? '—'}</div></F>
            <F label="Invoice No"><div className="text-slate-700">{g.invoiceNo ?? '—'}</div></F>
            <F label="Inbound Date"><div className="text-slate-700">{g.inboundDate?.slice(0, 10) ?? '—'}</div></F>
            <F label="Received By"><div className="text-slate-700">{g.receivedByName ?? '—'}</div></F>
          </div>

          <div className={`text-xs rounded px-3 py-2 ${isPutaway ? 'bg-amber-50 text-amber-800' : 'bg-blue-50 text-blue-800'}`}>
            {isPutaway
              ? 'Saving this screen POSTS the stock: Good goes to the bin you choose (sellable), Bad goes to the DAMAGE bin. Good + Bad must equal Received.'
              : 'This stock is already in inventory. Only Good stays available to sell — Damaged, Hold and Rejected are taken OUT of available stock. The four quantities must add up to Received, and anything not fully Passed needs a remark. Only the difference against putaway is moved; the quantity is never added again.'}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100 text-xs"><tr>
                <th className="p-2 text-left" style={{ minWidth: 200 }}>Product</th>
                <th className="text-left">Batch</th>
                <th className="text-right">Received</th>
                {isPutaway ? (
                  <><th className="text-right">Good</th><th className="text-right">Bad</th><th className="text-left">Bin</th></>
                ) : (
                  <>
                    <th className="text-right">Put away Good</th><th className="text-right">Put away Bad</th>
                    <th className="text-left">Result</th>
                    <th className="text-right">Good</th><th className="text-right">Damaged</th>
                    <th className="text-right">Hold</th><th className="text-right">Rejected</th>
                    <th className="text-left" style={{ minWidth: 180 }}>Remarks</th>
                  </>
                )}
                <th className="text-left">Stage</th>
              </tr></thead>
              <tbody>
                {items.map((i: any, idx: number) => {
                  const rowValid = lineValid(i);
                  const needsRemark = !isPutaway && Number(i.qcGoodQty || 0) !== Number(i.receivedQty) && !String(i.qcRemarks ?? '').trim();
                  return (
                    <tr key={i.id} className={`border-t ${rowValid ? '' : 'bg-rose-50'}`}>
                      <td className="p-2">{i.sku} — {i.name}</td>
                      <td className="font-mono text-xs">{i.batchNo || '—'}</td>
                      <td className="text-right">{i.receivedQty}</td>
                      {isPutaway ? (
                        <>
                          <td className="text-right">
                            <input type="number" min={0} max={i.receivedQty} disabled={viewOnly} value={i.goodQty}
                              onChange={(e) => setGood(idx, e.target.value)} className="w-16 border rounded px-1 text-sm text-right" />
                          </td>
                          <td className="text-right">
                            <input type="number" min={0} max={i.receivedQty} disabled={viewOnly} value={i.badQty}
                              onChange={(e) => setBad(idx, e.target.value)} className="w-16 border rounded px-1 text-sm text-right" />
                          </td>
                          <td><input value={i.bin ?? ''} disabled={viewOnly} onChange={(e) => setIt(idx, 'bin', e.target.value)} placeholder="A-01" className="w-20 border rounded px-1 text-sm font-mono" /></td>
                        </>
                      ) : (
                        <>
                          <td className="text-right text-slate-500">{i.postedGoodQty}</td>
                          <td className="text-right text-slate-500">{i.postedBadQty}</td>
                          <td>
                            {/* Picking an outcome drops the whole received quantity into that bucket;
                                the four number boxes then allow a partial split. */}
                            <select value={i.qcStatus ?? 'PASSED'} disabled={viewOnly} onChange={(e) => setQcStatus(idx, e.target.value)} className="border rounded px-1 text-sm">
                              {QC_OUTCOMES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </td>
                          {QC_BUCKETS.map((k) => (
                            <td key={k} className="text-right">
                              <input type="number" min={0} max={i.receivedQty} disabled={viewOnly} value={i[k] ?? 0}
                                onChange={(e) => setQcBucket(idx, k, e.target.value)} className="w-14 border rounded px-1 text-sm text-right" />
                            </td>
                          ))}
                          <td>
                            <input value={i.qcRemarks ?? ''} disabled={viewOnly} onChange={(e) => setIt(idx, 'qcRemarks', e.target.value)}
                              placeholder={needsRemark ? 'Required — why?' : 'Optional'}
                              className={`w-full border rounded px-1 text-sm ${needsRemark ? 'border-rose-400 bg-rose-50 placeholder-rose-500' : ''}`} />
                          </td>
                        </>
                      )}
                      <td className="text-xs"><Badge text={isPutaway ? (i.putawayStatus ?? 'PENDING') : (i.qcStatus ?? 'PENDING')} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="px-5 py-3 border-t flex justify-end gap-3">
          <button onClick={onClose} className="border px-4 py-1.5 rounded text-sm">Close</button>
          {!viewOnly && (
            <button onClick={() => save.mutate()} disabled={save.isPending || !valid}
              title={!valid ? (isPutaway ? 'Good + Bad must equal Received on every line' : 'Every line must add up to Received, and anything not fully Passed needs a remark') : undefined}
              className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">
              {save.isPending ? 'Saving…' : isPutaway ? 'Putaway → Stock' : 'Save QC'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
